import { useTranslation } from "react-i18next";
import { FieldIssue, ProviderFieldGroup } from "../ssoProviderModel";

export type FieldCopy = { label: string; help?: string };

export const useProviderFieldCopy = () => {
    const { t } = useTranslation();

    const fields: Record<string, FieldCopy> = {
        providerName: {
            label: t("settings.sso.providers.field.provider_name", "Provider name"),
            help: t(
                "settings.sso.providers.field.provider_name_help",
                "Short identifier used in the sign-in URL. It cannot be changed later.",
            ),
        },
        label: {
            label: t("settings.sso.connector_label", "Displayed name"),
            help: t(
                "settings.sso.providers.field.label_help",
                "Shown on the sign-in button. Switch the platform language to translate it.",
            ),
        },
        clientId: { label: t("settings.sso.providers.field.client_id", "Client ID") },
        clientSecret: { label: t("settings.sso.providers.field.client_secret", "Client secret") },
        urlAuthorize: {
            label: t("settings.sso.providers.field.url_authorize", "Authorization URL"),
            help: t("settings.sso.providers.field.url_authorize_help", "Where users are sent to sign in."),
        },
        urlAccessToken: {
            label: t("settings.sso.providers.field.url_access_token", "Token URL"),
            help: t("settings.sso.providers.field.url_access_token_help", "Where the authorization code is exchanged."),
        },
        urlResourceOwnerDetails: {
            label: t("settings.sso.providers.field.url_resource_owner_details", "User info URL"),
            help: t("settings.sso.providers.field.url_resource_owner_details_help", "Where the profile is read from."),
        },
        scopes: {
            label: t("settings.sso.providers.field.scopes", "Scopes"),
            help: t("settings.sso.providers.field.scopes_help", "Use the separator your provider expects."),
        },
        externalIdCookieName: {
            label: t("settings.sso.providers.field.external_id_cookie_name", "External ID cookie"),
            help: t(
                "settings.sso.providers.field.external_id_cookie_name_help",
                "When set, its value is appended to the user info URL.",
            ),
        },
        metadataUrl: {
            label: t("settings.sso.providers.field.metadata_url", "Discovery document"),
            help: t(
                "settings.sso.providers.field.metadata_url_help",
                "The provider's .well-known/openid-configuration URL. Endpoints and signing keys are read from it.",
            ),
        },
        redirectUrl: {
            label: t("settings.sso.providers.field.redirect_url", "Post-login redirect"),
            help: t("settings.sso.providers.field.redirect_url_help", "Where users land once signed in."),
        },
        resourceOwnerHeaders: {
            label: t("settings.sso.providers.field.resource_owner_headers", "User info request headers"),
        },
        resourceOwnerMapping: {
            label: t("settings.sso.providers.field.resource_owner_mapping", "Attribute mapping"),
            help: t(
                "settings.sso.providers.field.resource_owner_mapping_help",
                "Maps our fields to the ones your provider returns.",
            ),
        },
        cookiesToSaveOnPersistence: {
            label: t("settings.sso.providers.field.cookies_to_save", "Cookies kept after sign-in"),
        },
        idpEntityId: {
            label: t("settings.sso.providers.field.idp_entity_id", "Identity provider entity ID"),
            help: t("settings.sso.providers.field.idp_entity_id_help", "The issuer your provider announces."),
        },
        idpSsoUrl: { label: t("settings.sso.providers.field.idp_sso_url", "Identity provider sign-in URL") },
        idpSloUrl: { label: t("settings.sso.providers.field.idp_slo_url", "Identity provider sign-out URL") },
        idpCert: {
            label: t("settings.sso.providers.field.idp_cert", "Signing certificate"),
            help: t(
                "settings.sso.providers.field.idp_cert_help",
                "The public certificate used to verify assertions, without the BEGIN and END lines.",
            ),
        },
        spEntityId: {
            label: t("settings.sso.providers.field.sp_entity_id", "Our entity ID"),
            help: t("settings.sso.providers.field.sp_entity_id_help", "Declare this value on the provider's side."),
        },
        spAcsUrl: {
            label: t("settings.sso.providers.field.sp_acs_url", "Our assertion consumer URL"),
            help: t(
                "settings.sso.providers.field.sp_acs_url_help",
                "Where assertions are posted back. It must match the URL declared on the provider's side exactly.",
            ),
        },
        spSloUrl: { label: t("settings.sso.providers.field.sp_slo_url", "Our sign-out URL") },
        identifierAttribute: {
            label: t("settings.sso.providers.field.identifier_attribute", "Identifying attribute"),
            help: t(
                "settings.sso.providers.field.identifier_attribute_help",
                "The assertion attribute holding the user's email address.",
            ),
        },
        hostUrl: {
            label: t("settings.sso.providers.field.host_url", "CAS server URL"),
            help: t("settings.sso.providers.field.host_url_help", "Origin only, without a trailing path."),
        },
        loginUrl: {
            label: t("settings.sso.providers.field.login_url", "Sign-in path"),
            help: t("settings.sso.providers.field.path_help", "A path on the CAS server, starting with a slash."),
        },
        logoutUrl: {
            label: t("settings.sso.providers.field.logout_url", "Sign-out path"),
            help: t("settings.sso.providers.field.path_help", "A path on the CAS server, starting with a slash."),
        },
        validationServiceUrl: {
            label: t("settings.sso.providers.field.validation_service_url", "Ticket validation path"),
            help: t("settings.sso.providers.field.path_help", "A path on the CAS server, starting with a slash."),
        },
    };

    const groups: Record<ProviderFieldGroup, string> = {
        identity: t("settings.sso.providers.group.identity", "Identification"),
        button: t("settings.sso.providers.group.button", "Login page button"),
        credentials: t("settings.sso.providers.group.credentials", "Application credentials"),
        endpoints: t("settings.sso.providers.group.endpoints", "Provider endpoints"),
        idp: t("settings.sso.providers.group.idp", "Identity provider"),
        sp: t("settings.sso.providers.group.sp", "Our side"),
        server: t("settings.sso.providers.group.server", "CAS server"),
        advanced: t("settings.sso.providers.group.advanced", "Advanced options"),
    };

    const issues: Record<FieldIssue, string> = {
        required: t("settings.sso.providers.field_required", "This field is required."),
        url: t("settings.sso.providers.issue.url", "Enter a full URL starting with http:// or https://."),
        path: t("settings.sso.providers.issue.path", "Enter a path starting with a slash, not a full URL."),
        certificate: t("settings.sso.providers.issue.certificate", "This certificate looks too short to be valid."),
        duplicate: t("settings.sso.providers.duplicate_name", "A provider with this name already exists."),
        name: t("settings.sso.providers.issue.name", "The provider name cannot contain a slash."),
    };

    return { fields, groups, issues };
};
